import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  TableCellsIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function FileUploadSource({ type, companyId, onSuccess, onBack }) {
  const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: importing
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  const onDrop = useCallback(async (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    setName(uploadedFile.name.replace(/\.[^/.]+$/, ''));
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('name', uploadedFile.name);
      formData.append('companyId', companyId);
      
      const response = await api.post('/data-sources/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(response.data);
      setStep(2);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [companyId]);
  
  const acceptedTypes = type === 'csv' 
    ? { 'text/csv': ['.csv'] }
    : { 
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
      };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024 // 50MB
  });
  
  const handleImport = async () => {
    if (!preview?.dataSource?._id) {
      toast.error('No data source created');
      return;
    }
    
    setIsImporting(true);
    setStep(3);
    
    try {
      await api.post(`/data-sources/${preview.dataSource._id}/import`, { mode: 'full' });
      toast.success('Import started!');
      onSuccess(preview.dataSource);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to start import');
      setStep(2);
    } finally {
      setIsImporting(false);
    }
  };
  
  const Icon = type === 'csv' ? DocumentTextIcon : TableCellsIcon;
  
  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Preview' },
          { num: 3, label: 'Import' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 2 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="card p-8">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                : 'border-surface-300 dark:border-surface-700 hover:border-primary-400'
            }`}
          >
            <input {...getInputProps()} />
            
            {isUploading ? (
              <div>
                <ArrowPathIcon className="w-12 h-12 mx-auto mb-4 text-primary-500 animate-spin" />
                <p className="text-surface-600 dark:text-surface-400">Uploading and analyzing...</p>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-4 text-surface-400" />
                <p className="text-lg font-medium text-surface-900 dark:text-white mb-2">
                  {isDragActive ? 'Drop your file here' : 'Drag & drop a file here'}
                </p>
                <p className="text-surface-500 dark:text-surface-400 mb-4">
                  or click to browse
                </p>
                <p className="text-sm text-surface-400">
                  {type === 'csv' ? 'Supports CSV files' : 'Supports XLS, XLSX files'} • Max 50MB
                </p>
              </>
            )}
          </div>
          
          <div className="flex justify-between mt-6">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="text-lg font-medium bg-transparent border-b border-transparent hover:border-surface-300 focus:border-primary-500 outline-none text-surface-900 dark:text-white w-full"
                  placeholder="Data source name"
                />
                <p className="text-sm text-surface-500">
                  {preview.schema?.columns?.length} columns • {preview.schema?.totalRows?.toLocaleString()} rows
                </p>
              </div>
            </div>
            
            {/* Schema preview */}
            <div className="mb-6">
              <h3 className="font-medium text-surface-900 dark:text-white mb-3">Detected Schema</h3>
              <div className="flex flex-wrap gap-2">
                {preview.schema?.columns?.map((col, i) => (
                  <div key={i} className="bg-surface-100 dark:bg-surface-800 rounded-lg px-3 py-2 text-sm">
                    <span className="text-surface-900 dark:text-white font-medium">{col.name}</span>
                    <span className="text-surface-500 ml-2">{col.displayType || col.type}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Data preview table */}
            <h3 className="font-medium text-surface-900 dark:text-white mb-3">Data Preview</h3>
            <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
              <table className="w-full text-sm">
                <thead className="bg-surface-50 dark:bg-surface-800">
                  <tr>
                    {preview.schema?.columns?.map((col, i) => (
                      <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview?.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                      {preview.schema?.columns?.map((col, j) => (
                        <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate">
                          {String(row[col.name] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Back
            </button>
            <button onClick={handleImport} disabled={isImporting} className="btn-primary">
              {isImporting && <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />}
              Import Data
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Importing */}
      {step === 3 && (
        <div className="card p-12 text-center">
          <ArrowPathIcon className="w-12 h-12 mx-auto mb-4 text-primary-500 animate-spin" />
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            Importing Data...
          </h3>
          <p className="text-surface-500 dark:text-surface-400">
            Your data is being imported to BigQuery. This may take a few moments.
          </p>
        </div>
      )}
    </div>
  );
}

